// src/components/JarvislyModule/PageHeader/EditButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Button, Tooltip } from 'antd';
import translateX from 'utils/translateX';
import { useModuleStore } from 'stores/module';
import { EditOutlined } from '@ant-design/icons';
import { useModuleContext } from 'components/core/ModuleProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EditButton = ({
  actions = [],
  actionContext,
  label,
  tooltip,
  icon,
  type,
  disabled,
  onClick,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  const storedCurrentPage = useZustand('module', 'currentPage');
  const setModeStore = useZustand('module', 'setMode');
  const { viewMode: mode, setViewMode: setMode } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Check for actions - support both string and object formats
  const hasAction = key =>
    actions.includes(key) ||
    actions.some(a => typeof a === 'object' && a?.action === key);

  const showEditButton = hasAction('edit') || (hasAction('editRefresh') && mode === 'view');

  // Support disabled as function (receives { page, moduleContext })
  const resolvedDisabled =
    typeof disabled === 'function'
      ? disabled({ page: storedCurrentPage })
      : disabled;

  const computedDisabled =
    resolvedDisabled ?? (storedCurrentPage?.api?.returnAsObject && !storedCurrentPage?.data?._id);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultEdit = () => {
    if (typeof setMode === 'function') setMode('edit');
    else if (typeof setModeStore === 'function') setModeStore('edit');
  };

  const handleClick = () => setMode('edit');

  // Prevent blur from stealing focus before click registers
  const handleMouseDown = e => {
    e.preventDefault();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <EditOutlined />;
  const buttonType = type;
  const title = translateX(tooltip || label || 'edit');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showEditButton) return null;

  return (
    <Tooltip title={title} placement="top">
      <Button
        type={buttonType}
        style={computedDisabled ? { backgroundColor: '#ffffff' } : undefined}
        disabled={computedDisabled}
        icon={buttonIcon}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        {isLabelProvided(label) ? translateX(label) : null}
      </Button>
    </Tooltip>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EditButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isLabelProvided(label) {
  return !!label && typeof label === 'string';
}
